import { AxiosError } from 'axios';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useLocation, useNavigate } from 'react-router-dom';

import AppLoader from 'components/common/AppLoader';

import { FeatureFlag } from 'constants/featureFlag';

import FeatureFlagContext from 'contexts/FeatureFlagContext';

import useQueryProfile from 'hooks/useQueryProfile';

import { identifyUser, initAmplitude } from 'utils/amplitude';
import {
  isAuthCookiePresent,
  saveEmployeeIdCookie,
  saveRedirectCookie,
} from 'utils/auth';
import { createUserFromEmployee } from 'utils/featureFlag';
import * as Intercom from 'utils/intercom';
import * as Iterate from 'utils/iterate';
import { RootRoutes } from 'utils/routeGenerator';
import * as Rudderstack from 'utils/rudderstack';
import * as Sentry from 'utils/sentry';
import SmartLook from 'utils/smartlook';
import Sprig, { recordSprigUserAttributes } from 'utils/sprig';

import { Company, Employee } from './types';

type SprigInitArgs = {
  employee: Employee;
  company: Company;
};

interface CurrentUserContextInterface {
  company: Company;
  employee: Employee;
  refetchCurrentUser: () => void;
  newAccountsClaimsExperienceEnabled?: boolean;
}

const CurrentUserContext = createContext<CurrentUserContextInterface>({
  company: {} as Company,
  employee: {} as Employee,
  refetchCurrentUser: () => {},
  newAccountsClaimsExperienceEnabled: false,
});

const renderCurrentUserContext = ({
  company,
  employee,
  newAccountsClaimsExperienceEnabled,
  refetchCurrentUser,
  children,
}: {
  company: Company;
  employee: Employee;
  newAccountsClaimsExperienceEnabled: boolean;
  refetchCurrentUser: () => void;
  children: React.ReactNode;
}) => (
  <CurrentUserContext.Provider
    value={{
      employee,
      company,
      refetchCurrentUser,
      newAccountsClaimsExperienceEnabled,
    }}
  >
    {children}
  </CurrentUserContext.Provider>
);

export const CurrentUserContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [featureFlagInitialized, setFeatureFlagInitialized] = useState(false);
  const featureFlag = useContext(FeatureFlagContext);
  const isLoggedIn = isAuthCookiePresent();
  const handleError = useErrorHandler();
  const location = useLocation();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company>();
  const [employee, setEmployee] = useState<Employee>();
  const sprigInitialized = useRef<boolean>(false);

  const { pathname, search } = location;

  const formaPretaxEnabled: boolean = featureFlag.flag(
    FeatureFlag.FORMA_PRETAX_ACCOUNTS_CLAIMS_PAGE,
    false
  );

  const newAccountsClaimsExperience: boolean = featureFlag.flag(
    FeatureFlag.MX_NEW_VIEWS_ENABLED,
    false
  );

  const sprigUserResearchEnabled = featureFlag.flag(
    FeatureFlag.SPRIG_USER_RESEARCH,
    false
  );

  const newPretaxExperience: boolean = employee?.impersonated
    ? localStorage.getItem('pretax_v2') === 'true'
    : formaPretaxEnabled;

  const newAccountsClaimsExperienceEnabled = employee?.impersonated
    ? localStorage.getItem('mx_v2') === 'true' || newPretaxExperience
    : newAccountsClaimsExperience || formaPretaxEnabled;

  const handleIdentifySprigUser = useCallback(
    async ({ employee, company }: SprigInitArgs) => {
      if (sprigUserResearchEnabled && !sprigInitialized.current) {
        // Initialize Sprig
        await new Sprig();

        Sprig.identifyUser({
          userId: employee.id,
          userEmail: employee.email,
        });

        recordSprigUserAttributes({
          country: employee.settings.country,
          companyName: company.company_info.common_name,
          hasStoreEnabledWallet: employee.employee_wallets.some(
            (w) => w.company_wallet_configuration.is_twic_store_enabled
          ),
          onboardedAt: employee.activated_date || employee.updated,
        });

        // Ensure we don't do all this twice
        sprigInitialized.current = true;
      }
    },
    [sprigUserResearchEnabled]
  );

  const { isLoading: isProfileLoading, refetch: refetchCurrentUser } =
    useQueryProfile({
      isEnabled: isLoggedIn,
      use_v3_profile: newPretaxExperience || newAccountsClaimsExperienceEnabled,
      onSuccess: ({ employee, company }) => {
        saveEmployeeIdCookie(employee.id);
        setEmployee(employee);
        setCompany(company);
      },
      onError: (error: AxiosError) => handleError(error),
    });

  useEffect(() => {
    if (employee && company) {
      initAmplitude(employee);
      identifyUser(employee);
      Rudderstack.initRudderstack();
      Rudderstack.identifyRudderstackUser(employee);
      Sentry.setUser(employee);
      Intercom.initIntercom(employee, company);
      Iterate.initIterate(employee, company);
    }
  }, [employee, company]);

  /**
   * Record user attributes for Sprig user research.
   */
  useEffect(() => {
    if (employee && company) {
      handleIdentifySprigUser({
        employee,
        company,
      });
    }
  }, [employee, company, handleIdentifySprigUser]);

  useEffect(() => {
    (async () => {
      if (employee) {
        await featureFlag.setUser(createUserFromEmployee(employee));
        setFeatureFlagInitialized(true);
      }
    })();
  }, [featureFlag, employee]);

  useEffect(() => {
    if (employee && company?.id && featureFlagInitialized) {
      const smartlookEnabled = featureFlag.flag(FeatureFlag.SMART_LOOK, false);

      const user = {
        name: `${employee.first_name} ${employee.last_name}`,
        email: employee.email,
        company: company.id,
        location: employee.settings.address?.locality,
        state: employee.settings.address?.state,
        zip: employee.settings.address?.zip,
      };

      smartlookEnabled
        ? SmartLook.handleIdentify(employee.id, user)
        : SmartLook.handleDisable();
    }
  }, [employee, company?.id, featureFlag, featureFlagInitialized]);

  useEffect(() => {
    if (!isLoggedIn) {
      saveRedirectCookie(pathname + search);
      if (!isLoggedIn) {
        navigate(RootRoutes.login());
      }
    }
  }, [navigate, pathname, search, isLoggedIn]);

  const isLoading = isProfileLoading || !featureFlagInitialized;

  return (
    <AppLoader isLoading={isLoading}>
      {isLoading
        ? undefined
        : renderCurrentUserContext({
            employee: employee ? employee : ({} as Employee),
            company: company ? company : ({} as Company),
            newAccountsClaimsExperienceEnabled,
            refetchCurrentUser,
            children,
          })}
    </AppLoader>
  );
};

export default CurrentUserContext;
